/** Breadcrumb nav derived from the current /settings pathname. */

import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { useAgentCatalog } from '@/app/providers/AgentCatalogProvider';

interface Crumb {
  id: string;
  label: string;
}

const TAB_LABELS: Record<string, string> = {
  config: 'Config',
  prompt: 'Prompt',
  experiment: 'Experiment',
  tools: 'Tools',
  owners: 'Owners',
};

function buildCrumbs(pathname: string, agentName: (id: string) => string): Crumb[] {
  const crumbs: Crumb[] = [{ id: '/', label: 'Home' }];
  const segments = pathname.split('/').filter(Boolean);

  if (segments[0] !== 'settings') return crumbs;

  crumbs.push({ id: '/settings', label: 'Settings' });

  if (segments[1] === 'admin') {
    crumbs.push({ id: '/settings/admin', label: 'Admin' });
    return crumbs;
  }

  if (segments[1] === 'agents') {
    crumbs.push({ id: '/settings/agents', label: 'Agents' });
    const agentId = segments[2];
    if (agentId) {
      crumbs.push({ id: `/settings/agents/${agentId}`, label: agentName(agentId) });
      const tab = segments[3];
      if (tab && TAB_LABELS[tab]) {
        crumbs.push({
          id: `/settings/agents/${agentId}/${tab}`,
          label: TAB_LABELS[tab],
        });
      }
    }
    return crumbs;
  }

  return crumbs;
}

export function SettingsBreadcrumbsWidget() {
  const navigate = useNavigate();
  const location = useLocation();
  const { agents } = useAgentCatalog();

  const agentName = useMemo(() => {
    const byId = new Map(agents.map((a) => [a.id, a.displayName]));
    return (id: string): string => byId.get(id) ?? id;
  }, [agents]);

  const crumbs = useMemo(
    () => buildCrumbs(location.pathname, agentName),
    [location.pathname, agentName],
  );

  return (
    <nav aria-label="breadcrumbs" className="flex items-center gap-1 text-sm">
      {crumbs.map((c, i) => (
        <span key={c.id} className="flex items-center gap-1">
          {i > 0 && <ChevronRight className="size-3.5 text-muted-foreground" aria-hidden="true" />}
          <button
            type="button"
            onClick={() => navigate(c.id)}
            className={cn(
              'text-muted-foreground hover:text-foreground',
              i === crumbs.length - 1 && 'font-medium text-foreground',
            )}
          >
            {c.label}
          </button>
        </span>
      ))}
    </nav>
  );
}
